import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { Subject, catchError, finalize, forkJoin, of, switchMap } from 'rxjs';

import { IqObservationTilesComponent } from '../../components/iq-observation-tiles/iq-observation-tiles.component';
import { IqObservationsChartComponent } from '../../components/iq-observations-chart/iq-observations-chart.component';
import { IqTicketTilesComponent } from '../../components/iq-ticket-tiles/iq-ticket-tiles.component';
import { IqAggregatedUseCasePieChartComponent } from '../../components/iq-aggregated-use-case-pie-chart/iq-aggregated-use-case-pie-chart.component';
import {
  IqDashboardResponse,
  IqTicketStats,
} from '../../domain/iq-aggregated-dashboard.model';
import { IqAggregatedDashboardService } from '../../services';
import {
  IqAggregatedTimePreset,
  IqAggregatedTimeRange,
  getIqAggregatedTimeRange,
} from '../../utils/iq-aggregated-time-range';
import { ToggleComponent } from '../../../../shared/components/toggle/toggle.component';
import { SharedModule } from '../../../../shared/shared.module';
import { canUseSampleData } from '../../../../shared/utils/sample-data-toggle.util';
import * as fromStore from '../../../../store';

@Component({
  selector: 'app-iq-dashboard',
  templateUrl: './iq-dashboard.component.html',
  standalone: true,
  imports: [
    SharedModule,
    ToggleComponent,
    IqObservationTilesComponent,
    IqTicketTilesComponent,
    IqAggregatedUseCasePieChartComponent,
    IqObservationsChartComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IqDashboardComponent implements OnInit {
  private readonly iqAggregatedDashboardService = inject(IqAggregatedDashboardService);

  private readonly store = inject(Store);

  private readonly destroyRef = inject(DestroyRef);

  private readonly fetch$ = new Subject<void>();

  readonly dashboard = signal<IqDashboardResponse | null>(null);

  readonly ticketMetrics = signal<IqTicketStats | null>(null);

  readonly currentRange = signal<IqAggregatedTimeRange | null>(null);

  readonly selectedPreset = signal<IqAggregatedTimePreset>('1d');

  readonly isLoadingTicketMetrics = signal(false);

  readonly showMockData = signal(false);

  readonly canShowSampleDataToggle = signal(false);

  readonly isNoContent = signal(false);

  readonly isLoading = signal(false);

  readonly hasError = signal(false);

  // undefined = not yet seen; tracks the account so a genuine switch (not
  // just any currentUser emission) triggers a reload.
  private previousAccountName: string | null | undefined = undefined;

  ngOnInit(): void {
    this.fetch$
      .pipe(
        switchMap(() => {
          this.isLoading.set(true);
          this.isLoadingTicketMetrics.set(true);
          this.isNoContent.set(false);
          this.hasError.set(false);
          this.dashboard.set(null);
          this.ticketMetrics.set(null);

          const range = getIqAggregatedTimeRange(this.selectedPreset());
          this.currentRange.set(range);

          const dashboard$ = this.iqAggregatedDashboardService
            .getIqObservationsDashboard(
              range.begin,
              range.end,
              this.showMockData(),
            )
            .pipe(
              catchError(() => {
                this.hasError.set(true);
                return of(null);
              }),
            );

          const ticketMetrics$ = this.iqAggregatedDashboardService
            .getAggregatedIqTicketStats(range.begin, range.end, this.showMockData())
            .pipe(catchError(() => of(null)));

          return forkJoin({
            dashboard: dashboard$,
            ticketMetrics: ticketMetrics$,
          }).pipe(
            finalize(() => {
              this.isLoading.set(false);
              this.isLoadingTicketMetrics.set(false);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ dashboard, ticketMetrics }) => {
        this.dashboard.set(dashboard);
        this.ticketMetrics.set(ticketMetrics);
        this.isNoContent.set(dashboard === null && !this.hasError());
      });

    this.fetchDashboard();

    this.store
      .select(fromStore.getCurrentUser)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((user) => {
        this.canShowSampleDataToggle.set(canUseSampleData(user));

        // Route guards only re-run on navigation, so switching accounts
        // while already mounted here would otherwise leave the previous
        // account's dashboard on screen.
        const accountName = user?.Account?.Name ?? null;
        if (this.previousAccountName === undefined) {
          this.previousAccountName = accountName;
          return;
        }
        if (this.previousAccountName !== accountName) {
          this.previousAccountName = accountName;
          this.fetchDashboard();
        }
      });
  }

  onTimeSelectorChange(value: string): void {
    this.selectedPreset.set(value as IqAggregatedTimePreset);
    this.fetchDashboard();
  }

  toggleMockData(value: boolean): void {
    this.showMockData.set(value);
    this.fetchDashboard();
  }

  private fetchDashboard(): void {
    this.fetch$.next();
  }
}
